import { ReactElement, useMemo } from 'react';
import {
    UIPaginatedFilterItem,
    UIPaginatedFilterOption,
    UIPopoverPlacement
} from '@/ui';
import { useCompanyContext } from '@/companies/providers';
import { useTaskFilter } from '../providers';
import { useFetchTeamFilterOptions } from '@/common/queries';
import { useFilterPopupState } from '@/common/hooks';
import { useTasksTranslation } from '../hooks';

type TeamFilterItemProps = {
    popoverPlacement?: UIPopoverPlacement;
};

export function TeamFilterItem(props: TeamFilterItemProps): ReactElement {
    const { popoverPlacement } = props;

    const { t } = useTasksTranslation();
    const { company } = useCompanyContext();
    const { teamIds, onTeamFilterApplied } = useTaskFilter();
    const { search, setSearch, debouncedSearch, hasOpened, onOpenChanged } =
        useFilterPopupState();

    const {
        options,
        status,
        fetchNextPage,
        isFetchingNextPage,
        hasNextPage,
        refetch
    } = useFetchTeamFilterOptions({
        companyId: company.id,
        search: debouncedSearch,
        enabled: hasOpened
    });

    const items = useMemo<UIPaginatedFilterOption[]>(() => {
        return options.map((option) => ({
            id: option.id,
            text: option.name,
            image: option.image ?? undefined
        }));
    }, [options]);

    const selectedIds = useMemo(() => Array.from(teamIds), [teamIds]);

    return (
        <UIPaginatedFilterItem
            noneSelectedText={t('components.team_filter_item.none_selected')}
            emptyItemsText={t('components.team_filter_item.empty')}
            icon="globe"
            popoverPlacement={popoverPlacement}
            items={items}
            selectedIds={selectedIds}
            search={search}
            onSearchChange={setSearch}
            isLoading={hasOpened && status === 'pending'}
            isError={status === 'error'}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={hasNextPage}
            onLoadMore={fetchNextPage}
            onOpenChanged={onOpenChanged}
            onApply={onTeamFilterApplied}
            onRetry={refetch}
        />
    );
}
